import React, { useEffect, useMemo, useState } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Calendar as CalIcon, MapPin, Trophy, ChevronLeft, ChevronRight } from "lucide-react";

const CATEGORIES = ["Sub-12", "Sub-14", "Sub-16", "Sub-20", "Adulto"];
const CAT_COLOR = {
  "Sub-12": "hsl(199 90% 60%)", "Sub-14": "hsl(265 85% 65%)",
  "Sub-16": "hsl(82 90% 55%)", "Sub-20": "hsl(45 100% 60%)",
  "Adulto": "hsl(0 70% 60%)",
};
const MONTHS_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function todayIso() { return new Date().toISOString().slice(0, 10); }
function ageFromBirth(bd) {
  if (!bd) return null;
  try { return new Date().getFullYear() - new Date(bd + "T00:00:00").getFullYear(); }
  catch { return null; }
}

function pushLabel({ ids, push }) {
  if (!ids.length) return "—";
  if (push?.ok) return "enviado";
  if (push?.error === "no_subscribers") return "não enviado (atleta ainda não ativou as notificações no celular)";
  return "não enviado (erro no serviço de notificações)";
}

function emptyGame() {
  return { date: todayIso(), time: "19:00", location: "", competition: "", category: "Sub-16", gender: "F", notes: "" };
}

export default function AdminCalendar() {
  const [games, setGames] = useState([]);
  const [students, setStudents] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyGame());
  const [convOpen, setConvOpen] = useState(false);
  const [convGame, setConvGame] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const [g, s] = await Promise.all([api.get("/games"), api.get("/students")]);
      setGames(g.data);
      setStudents(s.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const monthGames = useMemo(() => {
    const prefix = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`;
    return games.filter(g => (g.date || "").startsWith(prefix));
  }, [games, cursor]);

  const gamesByDay = useMemo(() => {
    const map = {};
    for (const g of monthGames) {
      const d = parseInt((g.date || "").slice(8, 10), 10);
      if (!map[d]) map[d] = [];
      map[d].push(g);
    }
    return map;
  }, [monthGames]);

  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const firstWeekday = new Date(cursor.y, cursor.m, 1).getDay();
  const cells = Array.from({ length: firstWeekday + daysInMonth }, (_, i) =>
    i < firstWeekday ? null : i - firstWeekday + 1
  );

  const openCreate = (dateIso) => { setEditing(null); setForm({ ...emptyGame(), date: dateIso || todayIso() }); setEditOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ ...g }); setEditOpen(true); };

  const saveGame = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/games/${editing.id}`, form);
      else await api.post("/games", form);
      toast.success("Jogo salvo");
      setEditOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const delGame = async (g) => {
    const ok = await confirm({ title: "Excluir jogo?", description: `${g.competition} em ${g.date}. A convocação também será removida e os alunos convocados serão notificados.`, confirmText: "Excluir", destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/games/${g.id}`);
      toast.success("Jogo excluído");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openConvocation = (g) => {
    setConvGame(g);
    setSelectedIds((g.convocation || []).map(c => c.student_id));
    setPushResult(null);
    setConvOpen(true);
  };

  const toggleSelect = async (student) => {
    if (selectedIds.includes(student.id)) {
      setSelectedIds(selectedIds.filter(x => x !== student.id));
      return;
    }
    if (CATEGORIES.indexOf(student.category) > CATEGORIES.indexOf(convGame.category)) {
      const ok = await confirm({
        title: "Atleta fora da categoria",
        description: `A atleta "${student.name}" (${student.category}) está fora da categoria ${convGame.category}. Deseja convocá-la mesmo assim?`,
        confirmText: "Convocar mesmo assim",
      });
      if (!ok) return;
    }
    setSelectedIds([...selectedIds, student.id]);
  };

  const saveConvocation = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/games/${convGame.id}/convocation`, { student_ids: selectedIds });
      setPushResult(data.push);
      toast.success(`Convocação salva — ${data.push.added.ids.length} adicionado(s), ${data.push.removed.ids.length} removido(s)`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const navMonth = (delta) => {
    const d = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  // Alunos elegíveis para a convocação (mesma categoria + gênero)
  const eligible = useMemo(() => {
    if (!convGame) return [];
    return students
      .filter(s => s.status === "ativo" && (!s.gender || s.gender === convGame.gender))
      .sort((a, b) => {
        const ra = a.category === convGame.category ? -1 : CATEGORIES.indexOf(a.category);
        const rb = b.category === convGame.category ? -1 : CATEGORIES.indexOf(b.category);
        return ra - rb || a.name.localeCompare(b.name, "pt-BR");
      });
  }, [students, convGame]);

  return (
    <div data-testid="admin-calendar-page">
      <PageHeader eyebrow="Jogos" title="Calendário" subtitle="Cadastre jogos, convoque atletas e envie notificações automáticas." mascot={BRAND.MANGUITO_COACH} />

      <div className="vv-card p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navMonth(-1)} data-testid="prev-month"><ChevronLeft /></Button>
          <div className="text-xl font-black min-w-[150px] text-center capitalize">{MONTHS_PT[cursor.m]}/{cursor.y}</div>
          <Button variant="ghost" size="icon" onClick={() => navMonth(1)} data-testid="next-month"><ChevronRight /></Button>
        </div>
        <Button onClick={() => openCreate()} className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase" data-testid="new-game-button">
          <Plus className="size-4 mr-2" /> Adicionar jogo
        </Button>
      </div>

      <div className="vv-card p-2 sm:p-4 overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[720px]">
          {WEEKDAYS.map(w => <div key={w} className="text-center text-[10px] uppercase tracking-widest text-muted-foreground py-1">{w}</div>)}
          {cells.map((d, i) => (
            <div key={i} className={`min-h-[90px] rounded border p-1 ${d ? "border-border/60 bg-background/40" : "border-transparent"}`}>
              {d && (
                <>
                  <div className="text-xs font-bold text-muted-foreground flex items-center justify-between">
                    <span>{d}</span>
                    <button data-testid={`add-game-day-${d}`} className="opacity-50 hover:opacity-100 transition-opacity text-[hsl(var(--vv-lime))] text-base" onClick={() => openCreate(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`)} title="Adicionar jogo neste dia">+</button>
                  </div>
                  <div className="space-y-1 mt-1">
                    {(gamesByDay[d] || []).map(g => (
                      <button
                        key={g.id}
                        onClick={() => openEdit(g)}
                        className="w-full text-left text-[10px] px-1.5 py-1 rounded truncate font-bold hover:opacity-80 transition"
                        style={{ background: `${CAT_COLOR[g.category]}30`, color: CAT_COLOR[g.category], border: `1px solid ${CAT_COLOR[g.category]}50` }}
                        title={`${g.competition} - ${g.time}`}
                        data-testid={`game-cell-${g.id}`}
                      >
                        {g.time} {g.category}/{g.gender}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {monthGames.length === 0 ? (
        <div className="mt-4"><EmptyState title="Nenhum jogo neste mês" description="Clique em 'Adicionar jogo' para criar o primeiro." mascot={BRAND.MANGUITA} /></div>
      ) : (
        <div className="mt-6 space-y-3">
          <h3 className="font-black uppercase tracking-wider text-sm">Jogos do mês ({monthGames.length})</h3>
          {monthGames.map(g => (
            <div key={g.id} className="vv-card p-4" data-testid={`game-item-${g.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="vv-chip whitespace-nowrap font-bold" style={{ background: `${CAT_COLOR[g.category]}30`, color: CAT_COLOR[g.category] }}>
                      {g.category} / {g.gender === "F" ? "Fem." : "Masc."}
                    </span>
                    <span className="font-black">{g.competition}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 flex gap-4 flex-wrap">
                    <span className="inline-flex items-center gap-1"><CalIcon className="size-3.5" />{g.date} • {g.time}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="size-3.5" />{g.location}</span>
                    <span className="inline-flex items-center gap-1"><Users className="size-3.5" />{(g.convocation || []).length} convocado(s)</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => openConvocation(g)} data-testid={`open-convocation-${g.id}`} className="border-[hsl(var(--vv-yellow))/0.5] text-[hsl(var(--vv-yellow))] hover:bg-[hsl(var(--vv-yellow))] hover:text-[hsl(218_50%_10%)] font-bold uppercase">
                    <Trophy className="size-3.5 mr-1" /> Convocação
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(g)} data-testid={`edit-game-${g.id}`}><Pencil className="size-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delGame(g)} data-testid={`delete-game-${g.id}`}><Trash2 className="size-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Game Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="game-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar jogo" : "Novo jogo"}</DialogTitle>
            <DialogDescription>Dados do jogo e competição.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data</Label>
                <DatePicker value={form.date} onChange={(d) => setForm({ ...form, date: d })} testId="game-date" />
              </div>
              <div>
                <Label>Horário</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="game-time" />
              </div>
            </div>
            <div>
              <Label>Competição</Label>
              <Input value={form.competition} onChange={(e) => setForm({ ...form, competition: e.target.value })} placeholder="Ex: Copa Interior" data-testid="game-competition" />
            </div>
            <div>
              <Label>Local</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ex: Ginásio Municipal" data-testid="game-location" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="game-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gênero</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="game-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="F">Feminino</SelectItem>
                    <SelectItem value="M">Masculino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Observações (opcional)</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={saveGame} disabled={saving || !form.competition || !form.location} data-testid="save-game-button" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convocation Dialog */}
      <Dialog open={convOpen} onOpenChange={setConvOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="convocation-dialog">
          <DialogHeader>
            <DialogTitle>Convocação — {convGame?.competition}</DialogTitle>
            <DialogDescription>
              {convGame?.category} • {convGame?.gender === "F" ? "Feminino" : "Masculino"} • {convGame?.date} às {convGame?.time}
            </DialogDescription>
          </DialogHeader>
          {eligible.length === 0 ? (
            <div className="text-sm text-muted-foreground">Nenhum aluno ativo do mesmo gênero. Verifique o gênero no cadastro dos alunos.</div>
          ) : (
            <div className="divide-y divide-border max-h-[50vh] overflow-y-auto">
              {eligible.map((s, i) => {
                const age = ageFromBirth(s.birth_date);
                const checked = selectedIds.includes(s.id);
                const inCat = s.category === convGame.category;
                const showOthersHeader = !inCat && (i === 0 || eligible[i - 1].category === convGame.category);
                return (
                  <React.Fragment key={s.id}>
                  {i === 0 && inCat && <div className="px-2 pt-2 pb-1 text-[11px] uppercase tracking-widest text-[hsl(var(--vv-lime))]" data-testid="convoke-header-in-category">Categoria {convGame.category}</div>}
                  {showOthersHeader && <div className="px-2 pt-3 pb-1 text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="convoke-header-other-categories">Outras categorias</div>}
                  <label className="flex items-center justify-between p-2 hover:bg-muted/40 cursor-pointer" data-testid={`convoke-row-${s.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Checkbox checked={checked} onCheckedChange={() => toggleSelect(s)} data-testid={`convoke-check-${s.id}`} />
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{s.name}</div>
                        <div className="text-xs text-muted-foreground">{s.category} • {age != null ? `${age} anos` : "sem data nasc."} • {s.position || "—"}</div>
                      </div>
                    </div>
                  </label>
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {pushResult && (
            <div className="text-xs p-2 rounded bg-muted/40 border border-border">
              <div data-testid="push-result-added"><b>Adicionados:</b> {pushResult.added.ids.length} • push: {pushLabel(pushResult.added)}</div>
              <div data-testid="push-result-removed"><b>Removidos:</b> {pushResult.removed.ids.length} • push: {pushLabel(pushResult.removed)}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvOpen(false)}>Fechar</Button>
            <Button onClick={saveConvocation} disabled={saving} data-testid="save-convocation-button" className="bg-[hsl(var(--vv-yellow))] text-[hsl(218_50%_10%)] font-bold">
              {saving ? "Salvando..." : "Salvar convocação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
